import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react'
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import { privateApi } from '../../../api/api';
import FormSpinner from '../../../shared/components/spinner/FormSpinner';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { ErrorResponseSchema } from '../response/ErrorResponseSchema';
import { CheckAvailabilitySchema } from '../schema/CheckAvailabilitySchema';
import { CheckAvailabilityData } from '../types/CheckAvailabilityData';
import DateInput from '../../../shared/components/form/DateInput';

interface AvailabilityFormRestrictionsProps {
    field: any; // Use `any` for simplicity; replace with correct typing if needed
    control: any; // Use `any` for simplicity; replace with correct typing if needed
    watch: any; // Use `any` for simplicity; replace with correct typing if needed
    position: number; // Use `any` for simplicity; replace with correct typing if needed
    setValue?: any; // Use `any` for simplicity; replace with correct typing if needed
}

const AvailabilityFormRestrictions: React.FC<AvailabilityFormRestrictionsProps> = ({ field, control, watch, position, setValue }) => {
    const [showCheckAvailability, setShowCheckAvailability] = useState(false);

    const { data: roomTypes, isFetching, isSuccess } = useQuery({
        queryKey: ['rooms', 'types', 'restrictions'],
        queryFn: () => privateApi.get('/v4/room-type').then(res => {
            return res.data.data.map((roomType: any) => ({
                value: roomType.id,
                label: roomType.name,
            }));
        }),
    })

    const allFilled = watch(`${field}.values.room_type_id`) && watch(`${field}.values.date_from`) && watch(`${field}.values.date_to`) && watch(`${field}.values.value`);

    const { mutate } = useMutation({
        mutationFn: (data: CheckAvailabilityData) => {
            return privateApi.post('/v3/check-total-availability', data);
        },
        onSuccess: async (data) => {
            toast.success(
                <div className='message-toast'>
                    Availability Checked in Restriction Setup #{position + 1}, {data?.data?.message}
                </div>, {
                id: 'check-availability',
            });
        },
        onError: (error: AxiosError) => {
            if (error?.response) {
                try {
                    const validatedError = ErrorResponseSchema.parse(error.response.data);

                    const errorMessage = validatedError.message;
                    const statusCode = error.response.status;

                    console.log('Error', errorMessage);
                    console.log('Status Code', statusCode);

                    if (statusCode === 422) {
                        const errors = validatedError.errors;

                        const errorMessageWithLineBreaks = errors && Object.values(errors).map((line, index) => (
                            <React.Fragment key={index}>
                                {line}
                                <br />
                            </React.Fragment>
                        ));

                        toast.error(
                            <div className='message-toast'>
                                Please check the form for Restriction Setup #{position + 1} <br />
                                {errorMessageWithLineBreaks ? errorMessageWithLineBreaks : 'Unknown errors'}
                            </div>, {
                            id: 'check-availability',
                        })
                    } else {
                        toast.error(`${errorMessage} (Code: ${statusCode})`, {
                            id: 'check-availability',
                        });
                    }
                } catch (validationError) {
                    console.error('Error Response Validation Failed:', validationError);
                    toast.error(`Failed to process error response for Restriction Setup #${position + 1}`, {
                        id: 'check-availability',
                    });
                }
            } else {
                console.log('Unknown error', error);
                toast.error(`An unknown error occurred while checking availability for Restriction Setup #${position + 1}`, {
                    id: 'check-availability',
                });
            }
        },
    });

    const onCheckAvailability = useCallback(() => {
        toast.loading('Checking Availability...', {
            id: 'check-availability',
        });

        const validatedZod = CheckAvailabilitySchema.safeParse({
            availability: watch(`${field}.values.value`),
            date_from: watch(`${field}.values.date_from`),
            date_to: watch(`${field}.values.date_to`),
            room_type_id: watch(`${field}.values.room_type_id`).value,
        });

        if (!validatedZod.success) {
            const errorMessages = validatedZod.error.errors
                .map((error) => error.message)
                .join(', ');

            toast.error(
                <div className='message-toast'>
                    Invalid form data for Restriction Setup #{position + 1}: <br />
                    {errorMessages}
                </div>, {
                id: 'check-availability',
            });
            return;
        }

        mutate(validatedZod.data, {
            onSuccess: () => {
                setShowCheckAvailability(false);
                setValue(`${field}.values.is_checked_availability`, true);
            },
            onError: () => {
                setShowCheckAvailability(false);
            }
        });
    }, [mutate]);

    useEffect(() => {
        if (allFilled) {
            setShowCheckAvailability(true);
        }
    }, [watch(`${field}.values.room_type_id`), watch(`${field}.values.date_from`), watch(`${field}.values.date_to`), watch(`${field}.values.value`)]);

    return (
        <>
            {isFetching && <FormSpinner />}
            {isSuccess && (
                <div>
                    <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                            Room Title:
                        </label>
                        <div className="col-sm-8">
                            <Controller
                                name={`${field}.values.room_type_id`}
                                control={control}
                                render={({ field }) => (
                                    <Select
                                        {...field}
                                        options={roomTypes}
                                        value={roomTypes.find((option: { value: any; label: string }) => option.value === field.value)}
                                        placeholder="Select room title"
                                        onChange={(selectedOption) => {
                                            field.onChange(selectedOption);
                                        }}
                                        isLoading={isFetching}
                                    />
                                )}
                            />
                        </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                            From Date:
                        </label>
                        <div className="col-sm-8">
                            <Controller
                                name={`${field}.values.date_from`}
                                control={control}
                                render={({ field }) => (
                                    <DateInput
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                            To Date:
                        </label>
                        <div className="col-sm-8">
                            <Controller
                                name={`${field}.values.date_to`}
                                control={control}
                                render={({ field }) => (
                                    <DateInput
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                        }}
                                    />
                                )}
                            />
                        </div>
                    </div>

                    <div className="row mb-3 align-items-center">
                        <label className="col-sm-4 col-form-label d-flex justify-content-end">
                            Value:
                        </label>
                        <div className="col-sm-8">
                            <Controller
                                name={`${field}.values.value`}
                                control={control}
                                render={({ field }) => (
                                    <input
                                        type="number"
                                        className="form-control app-form-control rounded-1"
                                        placeholder='0'
                                        {...field}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                        }}
                                    />
                                )}
                            />
                            {showCheckAvailability && allFilled && (
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm mt-2"
                                    onClick={onCheckAvailability}
                                >
                                    Check Availability
                                </button>
                            )}
                        </div>
                    </div>

                    <Controller
                        name={`${field}.values.is_checked_availability`}
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                            <input type="hidden" {...field} />
                        )}
                    />
                </div>
            )}
        </>
    )
}

export default AvailabilityFormRestrictions